import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, fmtDate, API } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download, FileSpreadsheet, Plus, Trash2, Zap, Fuel } from "lucide-react";

function ym(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

export default function UsageLogsPage() {
    const { siteId } = useParams();
    const [site, setSite] = useState(null);
    const [month, setMonth] = useState(ym(new Date()));
    const [tab, setTab] = useState("BH"); // BH | DG
    const [bh, setBh] = useState([]);
    const [dg, setDg] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), start_time: "", end_time: "", what_ran: "", requested_by: "", note: "", reason: "" });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, b, d] = await Promise.all([
                api.get(`/sites/${siteId}`),
                api.get(`/sites/${siteId}/usage/beyond-hours`, { params: { month } }),
                api.get(`/sites/${siteId}/usage/dg`, { params: { month } }),
            ]);
            setSite(s.data); setBh(b.data); setDg(d.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteId, month]);
    useEffect(() => { load(); }, [load]);

    async function addBh(e) {
        e.preventDefault();
        try {
            await api.post(`/sites/${siteId}/usage/beyond-hours`, {
                date: form.date, start_time: form.start_time, end_time: form.end_time,
                what_ran: form.what_ran, requested_by: form.requested_by, note: form.note || null,
            });
            toast.success("Log added"); load();
            setForm({ ...form, what_ran: "", requested_by: "", note: "" });
        } catch (e) { toast.error(apiError(e)); }
    }
    async function addDg(e) {
        e.preventDefault();
        try {
            await api.post(`/sites/${siteId}/usage/dg`, {
                date: form.date, start_time: form.start_time, end_time: form.end_time, reason: form.reason,
            });
            toast.success("Log added"); load();
            setForm({ ...form, reason: "" });
        } catch (e) { toast.error(apiError(e)); }
    }
    async function del(kind, id) {
        try { await api.delete(`/sites/${siteId}/usage/${kind}/${id}`); load(); }
        catch (e) { toast.error(apiError(e)); }
    }

    async function download(kind) {
        const token = localStorage.getItem("fx_token");
        const url = `${API}/sites/${siteId}/usage/annexure/export.${kind}?month=${month}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = `usage-annexure-${site?.name}-${month}.${kind}`; a.click();
        URL.revokeObjectURL(a.href);
    }

    function shift(delta) {
        const [y, m] = month.split("-").map(Number);
        setMonth(ym(new Date(y, m - 1 + delta, 1)));
    }
    const label = useMemo(() => {
        const [y, m] = month.split("-");
        return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
    }, [month]);
    const bhTotal = useMemo(() => bh.reduce((s, r) => s + r.hours, 0).toFixed(2), [bh]);
    const dgTotal = useMemo(() => dg.reduce((s, r) => s + r.hours, 0).toFixed(2), [dg]);

    return (
        <div className="mx-auto max-w-5xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to="/" className="text-xs text-gray-500 hover:text-[#1E3355] inline-flex items-center gap-1 mb-3"><ChevronLeft className="w-3 h-3" /> All sites</Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Usage Logs</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">{site?.name || "…"}</h1>
                    {site?.beyond_hours_note && <div className="text-xs text-gray-500 mt-1 italic">Contract included hours: {site.beyond_hours_note}</div>}
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md p-1">
                        <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="usage-prev"><ChevronLeft className="w-4 h-4" /></button>
                        <div className="px-3 text-sm font-medium text-[#1E3355] min-w-[140px] text-center">{label}</div>
                        <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="usage-next"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                    <button onClick={() => download("csv")} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-white border border-gray-200 text-gray-700 hover:text-[#1E3355]" data-testid="usage-export-csv"><FileSpreadsheet className="w-3.5 h-3.5" /> CSV</button>
                    <button onClick={() => download("pdf")} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-[#1E3355] hover:bg-[#15243C] text-white" data-testid="usage-export-pdf"><Download className="w-3.5 h-3.5" /> Annexure PDF</button>
                </div>
            </div>

            <div className="mt-4 flex gap-1 bg-white border border-gray-200 rounded-md p-1 w-fit">
                <button onClick={() => setTab("BH")} className={`px-3 py-1.5 rounded text-sm font-medium inline-flex items-center gap-1.5 ${tab === "BH" ? "bg-[#1E3355] text-white" : "text-gray-600"}`} data-testid="tab-beyond-hours"><Zap className="w-3.5 h-3.5" /> Beyond-hours ({bhTotal}h)</button>
                <button onClick={() => setTab("DG")} className={`px-3 py-1.5 rounded text-sm font-medium inline-flex items-center gap-1.5 ${tab === "DG" ? "bg-[#1E3355] text-white" : "text-gray-600"}`} data-testid="tab-dg"><Fuel className="w-3.5 h-3.5" /> DG runtime ({dgTotal}h)</button>
            </div>

            {tab === "BH" && (
                <>
                    <form onSubmit={addBh} className="mt-4 rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid="add-beyond-hours-form">
                        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className={fieldCls} />
                            <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required className={fieldCls} data-testid="bh-start" />
                            <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required className={fieldCls} data-testid="bh-end" />
                            <input value={form.what_ran} onChange={(e) => setForm({ ...form, what_ran: e.target.value })} placeholder="What ran (AC / lights)" required className={fieldCls + " sm:col-span-2"} data-testid="bh-what" />
                            <input value={form.requested_by} onChange={(e) => setForm({ ...form, requested_by: e.target.value })} placeholder="Requested by" required className={fieldCls} data-testid="bh-by" />
                        </div>
                        <div className="mt-2 flex gap-2">
                            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note (optional)" className={fieldCls + " flex-1"} data-testid="bh-note" />
                            <button type="submit" className="inline-flex items-center gap-1 bg-[#1E3355] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="bh-submit"><Plus className="w-4 h-4" /> Add</button>
                        </div>
                    </form>
                    <LogTable rows={bh} kind="beyond-hours" del={del} testid="bh-table" columns={["date", "start_time", "end_time", "what_ran", "requested_by", "hours", "note"]} labels={["Date", "Start", "End", "What", "Requested by", "Hrs", "Note"]} total={bhTotal} />
                </>
            )}
            {tab === "DG" && (
                <>
                    <form onSubmit={addDg} className="mt-4 rounded-xl bg-white border border-gray-100 fx-shadow-card p-4" data-testid="add-dg-form">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className={fieldCls} />
                            <input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} required className={fieldCls} data-testid="dg-start" />
                            <input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} required className={fieldCls} data-testid="dg-end" />
                            <input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Reason" required className={fieldCls + " sm:col-span-2"} data-testid="dg-reason" />
                        </div>
                        <div className="mt-2"><button type="submit" className="inline-flex items-center gap-1 bg-[#1E3355] text-white text-sm font-semibold px-4 py-2 rounded-md" data-testid="dg-submit"><Plus className="w-4 h-4" /> Add DG entry</button></div>
                    </form>
                    <LogTable rows={dg} kind="dg" del={del} testid="dg-table" columns={["date", "start_time", "end_time", "reason", "hours"]} labels={["Date", "Start", "End", "Reason", "Hrs"]} total={dgTotal} />
                </>
            )}
        </div>
    );
}

function LogTable({ rows, kind, del, testid, columns, labels, total }) {
    return (
        <div className="mt-4 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
            <table className="w-full text-sm" data-testid={testid}>
                <thead className="bg-[#F4F6F9]">
                    <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                        {labels.map((l) => <th key={l} className="px-3 py-2">{l}</th>)}
                        <th className="px-3 py-2 text-right"></th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {rows.length === 0 ? <tr><td colSpan={columns.length + 1} className="p-6 text-center text-sm text-gray-500">No entries.</td></tr> :
                    rows.map((r) => (
                        <tr key={r.id} data-testid={`${testid}-row-${r.id}`}>
                            {columns.map((c) => <td key={c} className="px-3 py-2 text-xs text-gray-700">{c === "date" ? fmtDate(r[c]) : r[c] ?? "—"}</td>)}
                            <td className="px-3 py-2 text-right"><button onClick={() => del(kind, r.id)} className="p-1 text-gray-400 hover:text-[#E02B2B]"><Trash2 className="w-3.5 h-3.5" /></button></td>
                        </tr>
                    ))}
                    <tr className="bg-[#F4F6F9] font-semibold text-[#1E3355]">
                        <td colSpan={columns.length - 1} className="px-3 py-2 text-right">Total</td>
                        <td className="px-3 py-2 text-xs">{total} hrs</td>
                        <td />
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

const fieldCls = "rounded-md border border-gray-300 px-3 py-2 text-sm bg-white";
